import { useEffect, useState } from 'react';
import { Card, ErrorMessage, Loading, useAsync, useToast } from '../../components/ui';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import {
  COLOR_PROFILE_KEY,
  COLOR_PROFILES,
  DEFAULT_COLOR_PROFILE,
  applyColorProfile,
  isColorProfile,
  labelFor,
  type ColorProfile,
} from '../../lib/colorProfile';

/**
 * Farbprofil der Organisation.
 *
 * Die Auswahl greift sofort auf die ganze Oberfläche durch – anders ließe
 * sich nicht beurteilen, ob eine Farbe zum Haus passt: Ein Farbfeld von
 * 40 Pixeln sagt darüber nichts. Bis zum Speichern ist das aber nur eine
 * Vorschau, und der Hinweis darunter sagt das auch. Wer die Seite ohne zu
 * speichern verlässt, sieht beim nächsten Laden wieder das hinterlegte Profil.
 */
export function AppearanceSettings() {
  const { notify } = useToast();
  const { user, tenant, refreshUser } = useAuth();
  const settings = useAsync(() => api.get('/api/catalogs/settings'), []);

  const [gespeichert, setGespeichert] = useState<ColorProfile>(DEFAULT_COLOR_PROFILE);
  const [gewaehlt, setGewaehlt] = useState<ColorProfile>(DEFAULT_COLOR_PROFILE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!settings.data) return;
    const wert = settings.data[COLOR_PROFILE_KEY];
    const profil = isColorProfile(wert) ? wert : DEFAULT_COLOR_PROFILE;
    setGespeichert(profil);
    setGewaehlt(profil);
  }, [settings.data]);

  // Beim Verlassen der Seite die Vorschau zurücknehmen. Ohne das behielte die
  // Anwendung eine Farbe, die nirgends hinterlegt ist – bis zum nächsten Laden.
  // Zurück auf das, was für dieses Konto gilt: das eigene Profil, sonst das der Organisation.
  useEffect(() => () => applyColorProfile(user?.color_profile ?? tenant?.colorProfile), [user?.color_profile, tenant?.colorProfile]);

  if (settings.loading) return <Loading />;

  const waehlen = (profil: ColorProfile) => {
    setGewaehlt(profil);
    applyColorProfile(profil);
  };

  const speichern = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.put('/api/catalogs/settings', { ...settings.data, [COLOR_PROFILE_KEY]: gewaehlt });
      setGespeichert(gewaehlt);
      // Den Mandanten neu laden, damit das Profil auch nach einem Neuladen
      // der Seite steht und nicht nur als Vorschau im Wurzelelement hängt.
      await refreshUser();
      notify(`Standard-Farbprofil „${labelFor(gewaehlt)}“ gespeichert.`, 'success');
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  const verwerfen = () => {
    setGewaehlt(gespeichert);
    applyColorProfile(gespeichert);
  };

  const geaendert = gewaehlt !== gespeichert;

  return (
    <div className="stack">
      <ErrorMessage error={error} />

      <Card
        title="Farbprofil"
        subtitle="Standard für alle Benutzer, die noch kein eigenes Profil gewählt haben – also auch für die erste Anmeldung. Jeder Benutzer kann es unter „Profil & Darstellung“ für sich ändern."
        actions={
          geaendert ? (
            <div className="row" style={{ gap: 8 }}>
              <button className="btn sm" onClick={verwerfen} disabled={busy}>
                Verwerfen
              </button>
              <button className="btn primary sm" onClick={speichern} disabled={busy}>
                Speichern
              </button>
            </div>
          ) : undefined
        }
      >
        <div className="stack">
          <div className="farbprofil-wahl">
            {COLOR_PROFILES.map((profil) => (
              <button
                key={profil.key}
                type="button"
                className={`farbprofil-karte farbprofil-${profil.key}${gewaehlt === profil.key ? ' active' : ''}`}
                onClick={() => waehlen(profil.key)}
                aria-pressed={gewaehlt === profil.key}
              >
                <span className="farbprofil-name">
                  {profil.label}
                  {profil.key === DEFAULT_COLOR_PROFILE && <span className="small subtle"> · Standard</span>}
                </span>
                <span className="farbprofil-probe" />
              </button>
            ))}
          </div>

          {geaendert && (
            <div className="note-box tone-info">
              <strong>Vorschau</strong>
              <p>
                Die Oberfläche zeigt bereits „{labelFor(gewaehlt)}“, hinterlegt ist aber weiterhin
                „{labelFor(gespeichert)}“. Erst mit „Speichern“ gilt sie als Standard der Organisation.
              </p>
            </div>
          )}

          <div className="note-box tone-info">
            <strong>Was das Farbprofil nicht verändert</strong>
            <p>
              Die farbigen Kennzeichen der Seitenköpfe behalten ihre Farbe – die Disposition bleibt türkis, die
              Auswertungen violett, das Audit-Log rot. Ebenso alle Statusfarben: Erfolg bleibt grün, Warnung orange,
              Fehler und Fristverletzung rot. Auch das Markenband über der Kopfzeile bleibt das Produktlogo. Das
              Farbprofil trägt die Bedienung, nicht die Bedeutung.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
